/**
 * The engagement gate's challenge: the selected word among orthographic
 * near-misses, as a row of chips. One component for every shell — retells'
 * gloss popover, lekterion's selection menu, and the drawers apps/reader will
 * add (PORT-PREP "one challenge, three shells"). It replaces retells'
 * `GateChallenge` and the chip row of lekterion's `EngagementGate`.
 *
 * Props-only and hosting-agnostic: the shell owns what is withheld, the unlock,
 * the logging and the rubric around the row; this owns the round. A wrong pick
 * runs `useQuizCooldown` with `GATE_COOLDOWN` (shake — harder from the second
 * miss — fade, then a fresh shuffled set); a correct one is reported and the
 * shell unmounts the gate.
 *
 * Unstyled on purpose. It carries no Tailwind classes, so an app does not need
 * an `@source` for this file (apps/retells/src/index.css) — the shell passes
 * its own classes and styles the cooldown off the data attributes:
 *   - group `data-phase`: `idle` | `shaking` | `faded` | `staggerReturn`
 *   - group `data-shake`: `light` | `hard`, only while shaking
 *   - chip `data-locked`: present during the cooldown
 */

import { useCallback, useState, type JSX } from "react";

import type { NormalizeWord } from "@bindery/shared/reader/confounders";
import { buildChallenge, type GatePick } from "@bindery/shared/reader/gate";
import {
  GATE_COOLDOWN,
  useQuizCooldown,
  type CooldownTiming,
} from "@bindery/ui/hooks/use-quiz-cooldown";

export interface GateChallengeProps {
  /** The selected word, as it appears in the text. */
  word: string;
  /**
   * `challengeableConfounders(word, …)` — re-surfaced, collision-free, ranked.
   * Non-empty: a word with none falls open, and the shell renders no gate.
   */
  forms: readonly string[];
  /** The shell's dict normalizer, for the pick's `chosen`. */
  normalize: NormalizeWord;
  /** Language of the word, for the chips' `lang`. */
  lang: string;
  /** Accessible name of the chip group: what a match unlocks. */
  label: string;
  onPick: (pick: GatePick) => void;
  className?: string;
  chipClassName?: string;
  timing?: CooldownTiming;
}

export function GateChallenge({
  word,
  forms,
  normalize,
  lang,
  label,
  onPick,
  className,
  chipClassName,
  timing = GATE_COOLDOWN,
}: GateChallengeProps): JSX.Element {
  const [chips, setChips] = useState(() => buildChallenge(word, forms));
  // Every round deals the same number of chips with the same position ids,
  // so React updates each button's text in place and a reader's focus rides
  // through the reshuffle onto the new round rather than being dropped.
  const deal = useCallback(() => {
    setChips(buildChallenge(word, forms));
  }, [word, forms]);
  const cooldown = useQuizCooldown({ timing, onReshuffle: deal });

  return (
    <div
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- a row of buttons laid out as a plain flex box; <fieldset> is the element for form controls and brings its own UA rendering (min-inline-size, legend)
      role="group"
      aria-label={label}
      className={className}
      data-phase={cooldown.phase}
      data-shake={
        cooldown.phase === "shaking"
          ? cooldown.wrongCount >= timing.hardAfter
            ? "hard"
            : "light"
          : undefined
      }
    >
      {chips.map((chip) => (
        <button
          key={chip.id}
          type="button"
          lang={lang}
          className={chipClassName}
          // `aria-disabled`, never `disabled`: a disabled element cannot hold
          // focus, so disabling the set for the cooldown blew the reader's
          // focus out to <body> on every wrong pick. Each chip stays
          // focusable while the press is ignored; the shell's styles use
          // `data-locked` to drop the hover state that would invite one.
          aria-disabled={cooldown.lockedOut}
          data-locked={cooldown.lockedOut ? "" : undefined}
          onClick={() => {
            if (cooldown.lockedOut) return;
            onPick({
              form: chip.form,
              chosen: normalize(chip.form),
              correct: chip.correct,
            });
            if (chip.correct) cooldown.triggerCorrect();
            else cooldown.triggerWrong();
          }}
        >
          {chip.form}
        </button>
      ))}
    </div>
  );
}
